import React, { useState } from 'react'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'

import AuthService from '../../../utilities/services/auth.service'

import { capitalizeString } from '../../../utilities/helpers'

import {
  refundReleaseOrder,
  releaseSeasonPackageOrder,
  voidFlexPackOrder,
} from '../../../utilities/api'

import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'

import { Spinner } from '../../LoadingContainer/Spinner'
import Success from '../Success/Success'
import Error from '../Error/Error'

export default function ConfirmReleaseRefundOrder({
  order,
  isRefunding = false,
  isRenewing = false,
  isCancelling = false,
  isVoiding = false,
  onSuccess,
}) {
  const isSupplementalCustomFee = order?.type === 'customFeeAddition'
  const { closeModal } = useModal()
  const [isSaving, setIsSaving] = useState(false)
  const [modalState, setModalState] = useState('base')
  const [errorDescription, setErrorDescription] = useState('')
  const organization = AuthService.getOrg()
  const hasPaymentIntent = Boolean(
    order?.paymentIntentId && String(order.paymentIntentId).trim() !== ''
  )
  const isAccessPass = Boolean(
    order?.access_pass_template || order?.access_passes?.length
  )

  const request = isVoiding
    ? voidFlexPackOrder
    : isRenewing
      ? releaseSeasonPackageOrder
      : refundReleaseOrder

  const onConfirm = async () => {
    setIsSaving(true)
    const data = {}
    if (isRenewing) {
      data.orderId = order.id
    } else {
      data.order = order
      data.organizationUUID = organization?.[0]?.uuid
    }

    await request(data)
      .then((res) => {
        onSuccess()
        setModalState('success')
      })
      .catch((err) => {
        console.log('err: ', err)
        setErrorDescription(
          err?.response?.data?.error?.message ||
            err?.response?.data?.message ||
            err?.message ||
            ''
        )
        setModalState('error')
      })
  }

  if (modalState === 'error') {
    return (
      <Error
        title={`${
          isVoiding
            ? 'Error voiding order...'
            : isRefunding
              ? 'Error refunding order...'
              : isCancelling
                ? 'Error cancelling order...'
                : 'Error releasing tickets...'
        }`}
        description={errorDescription}
        onClose={closeModal}
      />
    )
  }

  if (modalState === 'success') {
    return (
      <Success
        title={`${
          isVoiding
            ? hasPaymentIntent
              ? 'Order refunded and vouchers removed!'
              : 'Order voided and vouchers removed!'
            : isRefunding
              ? 'Order refunded!'
              : isCancelling
                ? 'Order cancelled!'
                : 'Tickets released!'
        }`}
        description=""
        onClose={closeModal}
      />
    )
  }

  return (
    <Modal.Body className="d-flex flex-column align-items-center justify-content-center text-center">
      <div className="modal-body-heading">
        <h1 className="modal-body-heading-title m-0">
          Are you sure you want to{' '}
          {isVoiding
            ? 'void this flex pack order'
            : isRefunding
              ? 'refund this order'
              : isCancelling
                ? 'cancel this order'
                : 'release these tickets'}
          ?
        </h1>
      </div>
      <p className="text-muted mb-3">
        {isVoiding
          ? hasPaymentIntent
            ? 'This will refund the original payment method, remove all vouchers from this order, and mark the order as refunded. This action is final and cannot be undone.'
            : 'This order has no payment intent. Vouchers will be removed and the order will be cancelled (released). This action is final and cannot be undone.'
          : isSupplementalCustomFee
            ? 'This refunds only this supplemental custom-fee charge. The original package order and its seats will not be changed. Card and terminal payments are returned to the original payment method. This action is final and cannot be undone.'
            : isAccessPass
              ? 'This will cancel the order and revoke the access pass so it can no longer be used. If payment was made, a full refund will be processed to the original payment method. This action is final and cannot be undone. Please confirm all details before proceeding.'
              : 'This will cancel the order and release the tickets for other customers to purchase. If payment was made, a full refund will be processed to the original payment method. This action is final and cannot be undone. Please confirm all details before proceeding.'}
      </p>
      <p>
        {order?.event ? (
          <>
            <span className="fw-semi-bold">Event: </span>
            <span>{order?.event?.name}</span>
          </>
        ) : order?.package ? (
          <>
            <span className="fw-semi-bold">Package: </span>
            <span>{order?.package?.name}</span>
          </>
        ) : isAccessPass ? (
          <>
            <span className="fw-semi-bold">Access Pass: </span>
            <span>
              {order?.access_pass_template?.name ||
                order?.access_passes?.[0]?.name}
            </span>
          </>
        ) : (
          <>
            <span className="fw-semi-bold">Flex Pack: </span>
            <span>{order?.flex_pack?.name}</span>
          </>
        )}
      </p>
      <p>
        <span className="fw-semi-bold">Order ID: </span>
        <span>{order?.orderId}</span>
      </p>
      <p>
        <span className="fw-semi-bold">Customer: </span>
        <span>
          {(order?.firstName && order?.lastName) ||
          (order?.users_permissions_user?.firstName &&
            order?.users_permissions_user?.lastName)
            ? `${capitalizeString(`${order?.firstName ?? order?.users_permissions_user?.firstName} ${order?.lastName ?? order?.users_permissions_user?.lastName}`)}`
            : 'No User'}{' '}
          ({order?.email ?? order?.users_permissions_user?.email})
        </span>
      </p>
      <Stack className="btn-group-flex">
        <Button
          variant="outline-light"
          size="lg"
          className="btn-width text-danger"
          onClick={onConfirm}
          disabled={isSaving}
        >
          {isSaving ? (
            <Spinner variant="dark" />
          ) : (
            <>
              {isVoiding
                ? 'Void order'
                : isRefunding
                  ? 'Refund order'
                  : isCancelling
                    ? 'Cancel order'
                    : 'Release tickets'}
            </>
          )}
        </Button>
        <Button size="lg" onClick={closeModal}>
          Cancel
        </Button>
      </Stack>
    </Modal.Body>
  )
}
